import type { AnyElysia } from 'elysia'

import type { EdenFetchError } from '../../errors'
import type { EdenRequestParams } from '../../resolve'
import type { Observable, Observer } from './observable'

export type OperationType = 'query' | 'mutation' | 'subscription'

export interface OperationContext extends Record<string, unknown> {}

export type Operation<T extends AnyElysia = any> = {
  id: number
  type: OperationType
  params: EdenRequestParams<T>
  context: OperationContext
}

export type OperationLink<
  TElysia extends AnyElysia = AnyElysia,
  TInput = unknown,
  TOutput = unknown,
> = (
  options: OperationLinkOptions<TElysia, TInput, TOutput>,
) => OperationResultObservable<TElysia, TOutput>

export type OperationLinkOptions<
  TElysia extends AnyElysia = AnyElysia,
  _TInput = unknown,
  TOutput = unknown,
> = {
  operation: Operation<TElysia>
  next: (operation: Operation<TElysia>) => OperationResultObservable<TElysia, TOutput>
}

export type OperationResultObservable<TElysia extends AnyElysia, TOutput> = Observable<
  OperationResultEnvelope<TOutput>,
  EdenClientError<TElysia>
>

export type OperationResultObserver<TRoute extends AnyElysia, TOutput> = Observer<
  OperationResultEnvelope<TOutput>,
  EdenClientError<TRoute>
>

export type OperationResultEnvelope<T = any> = (EdenResultMessage<T> | EdenSuccessResponse<T>) & {
  context?: OperationContext
}

export type EdenResultMessage<T> =
  | { type: 'started'; data?: never }
  | { type: 'stopped'; data?: never }
  | { data: T; type: 'data' }

export type EdenSuccessResponse<T> = { data: T; type?: 'data' }

export type EdenLink<T extends AnyElysia = any, TMeta = {}> = ((
  opts: EdenClientRuntime,
) => OperationLink<T>) &
  TMeta

export type EdenClientRuntime = {}

export type EdenClientOptions<T extends AnyElysia> = {
  links: EdenLink<T>[]
}

/**
 * Type representing errors that can occur during Eden client operations.
 * This is the union of all possible error types from the Elysia route schema.
 *
 * @template _T - The Elysia app type (reserved for future use with route-specific error types)
 */
export type EdenClientError<_T extends AnyElysia = AnyElysia> = EdenFetchError<number, unknown> | Error

export class OperationError extends Error {
  constructor(message?: string) {
    super(message)
  }
}
